import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildManagerWithObjectsDocument, openPrintWindow } from './PayrollRegisterCard';

const API_BASE = 'http://localhost:3001';
const monthNames = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const shortMonthNames = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
const now = new Date();
const defaultPeriod = (() => {
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return {
    month: prev.getMonth() + 1,
    year: prev.getFullYear(),
  };
})();
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');
const registerTypeLabels = {
  standard: 'Ведомость сотрудников',
  manager: 'Ведомость менеджера',
};
const registerTypeBadges = {
  standard: { bg: '#ecfdf5', color: '#065f46' },
  manager: { bg: '#ede9fe', color: '#4c1d95' },
};
const registerStatusLabels = {
  draft: 'Черновик',
  formed: 'Сформирована',
  issued: 'Выдано',
};
const registerStatusBadges = {
  draft: { bg: '#f1f5f9', color: '#0f172a' },
  formed: { bg: '#dcfce7', color: '#15803d' },
  issued: { bg: '#dbeafe', color: '#1d4ed8' },
};
const finalRegisterStatuses = new Set(['formed', 'issued']);

const buildSummaryMap = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    if (!item || (item.registerType || 'standard') === 'manager') return;
    const status = String(item.status || '').toLowerCase();
    if (!finalRegisterStatuses.has(status)) return;
    const cardId = Number(item.cardId) || 0;
    if (!cardId) return;
    const entry = map.get(cardId) || {
      cardId,
      objectName: item.objectName || '',
      objectCode: item.objectCode || '',
      totalPaidCard: 0,
      totalAdvance: 0,
      totalIncome: 0,
      totalToPay: 0,
    };
    entry.objectName = entry.objectName || item.objectName || '';
    entry.objectCode = entry.objectCode || item.objectCode || '';
    entry.totalPaidCard = round2(entry.totalPaidCard + (Number(item.totalPaidCard) || 0));
    entry.totalAdvance = round2(entry.totalAdvance + (Number(item.totalAdvance) || 0));
    entry.totalIncome = round2(entry.totalIncome + (Number(item.totalIncome) || 0));
    entry.totalToPay = round2(entry.totalToPay + (Number(item.totalToPay) || 0));
    map.set(cardId, entry);
  });
  return map;
};

const isManagerRegister = (item) => String(item?.registerType || '').trim().toLowerCase() === 'manager';

const currency = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const columnFilterShape = {
  id: '',
  object: '',
  manager: '',
  period: '',
  type: '',
  status: '',
  amount: '',
  note: '',
};

export default function PayrollRegistersPage() {
  const navigate = useNavigate();
  const token = useMemo(() => (typeof window !== 'undefined' ? localStorage.getItem('authToken') : ''), []);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [columnFilters, setColumnFilters] = useState(() => ({ ...columnFilterShape }));
  const [reportMonth, setReportMonth] = useState(defaultPeriod.month);
  const [reportYear, setReportYear] = useState(defaultPeriod.year);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reportLoading, setReportLoading] = useState(false);

  const shiftReportPeriod = useCallback((delta) => {
    setReportMonth((prevMonth) => {
      let nextMonth = (Number(prevMonth) || 1) + delta;
      let nextYear = Number(reportYear) || now.getFullYear();
      if (nextMonth < 1) {
        nextMonth = 12;
        nextYear -= 1;
      } else if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
      }
      setReportYear(nextYear);
      return nextMonth;
    });
  }, [reportYear]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/api/payrolls`, { headers: { ...authHeaders } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'load_failed');
      const items = Array.isArray(data.items) ? data.items : [];
      setList(items.sort((a, b) => Number(new Date(b.createdAt || 0)) - Number(new Date(a.createdAt || 0))));
    } catch (e) {
      console.error('payroll journal load error', e);
      setError('Не удалось загрузить журнал.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadList(); }, [loadList, reloadKey]);

  const normalizedColumnFilters = useMemo(() => {
    const normalized = {};
    Object.entries(columnFilters).forEach(([key, value]) => {
      normalized[key] = String(value || '').toLowerCase().trim();
    });
    return normalized;
  }, [columnFilters]);

  const filteredList = useMemo(() => {
    const nf = normalizedColumnFilters;
    return list.filter((item) => {
      const idText = String(item.id || '').toLowerCase();
      const objectText = `${item.objectName || ''} ${(item.objectCodeFull || item.objectCode || '')}`.toLowerCase();
      const managerText = String(item.managerName || '').toLowerCase();
      const periodText = `${shortMonthNames[(item.periodMonth || 1) - 1] || ''} ${item.periodYear || ''}`.toLowerCase();
      const typeLabel = registerTypeLabels[item.registerType || 'standard'] || registerTypeLabels.standard;
      const typeText = `${String(item.registerType || '')} ${typeLabel}`.toLowerCase();
      const statusLabel = registerStatusLabels[item.status || 'draft'] || registerStatusLabels.draft;
      const statusText = `${String(item.status || '')} ${statusLabel}`.toLowerCase();
      const amountText = String(item.totalToPay || '').toLowerCase();
      const noteText = `${item.notes || ''} ${item.title || ''}`.toLowerCase();
      return (
        (!nf.id || idText.includes(nf.id)) &&
        (!nf.object || objectText.includes(nf.object)) &&
        (!nf.manager || managerText.includes(nf.manager)) &&
        (!nf.period || periodText.includes(nf.period)) &&
        (!nf.type || typeText.includes(nf.type)) &&
        (!nf.status || statusText.includes(nf.status)) &&
        (!nf.amount || amountText.includes(nf.amount)) &&
        (!nf.note || noteText.includes(nf.note))
      );
    });
  }, [list, normalizedColumnFilters]);

  const totals = useMemo(() => filteredList.reduce((acc, item) => acc + (Number(item.totalToPay) || 0), 0), [filteredList]);

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };
  const resetColumnFilters = () => setColumnFilters({ ...columnFilterShape });

  const fillFilterByCell = (key, rawValue, event) => {
    if (event?.ctrlKey) {
      event.preventDefault();
      handleColumnFilterChange(key, rawValue || '');
      return true;
    }
    return false;
  };

  const handleOpen = (id) => {
    if (!id) return;
    navigate(`/payrolls/${id}`);
  };

  const handleCreate = (type = 'standard') => {
    const suffix = type === 'manager' ? '?type=manager' : '';
    navigate(`/payrolls/new${suffix}`);
  };

  const handleRefresh = () => setReloadKey((prev) => prev + 1);

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm('Удалить ведомость без возможности восстановления?')) return;
    setDeletingId(id);
    try {
      const resp = await fetch(`${API_BASE}/api/payrolls/${id}`, { method: 'DELETE', headers: { ...authHeaders } });
      if (!resp.ok) throw new Error('delete_failed');
      handleRefresh();
    } catch (e) {
      console.error('delete payroll error', e);
      alert('Не удалось удалить ведомость.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenMonthlyReport = useCallback(async () => {
    setReportLoading(true);
    try {
      let dataset = list;
      try {
        const params = new URLSearchParams();
        if (reportMonth) params.set('month', reportMonth);
        if (reportYear) params.set('year', reportYear);
        const resp = await fetch(`${API_BASE}/api/payrolls?${params.toString()}`, { headers: { ...authHeaders } });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || 'load_failed');
        dataset = Array.isArray(data.items) ? data.items : list;
      } catch (fetchErr) {
        console.warn('monthly report fetch failed', fetchErr);
      }

      // merge with locally loaded list for the same period to avoid missing registers
      const mergedById = new Map();
      const appendDataset = (source) => {
        (source || []).forEach((item) => {
          if (!item || item.id === undefined || item.id === null) return;
          const id = Number(item.id);
          if (!Number.isFinite(id)) return;
          mergedById.set(id, item);
        });
      };
      appendDataset(dataset);
      const fallbackPeriodItems = list.filter((item) => Number(item.periodMonth) === Number(reportMonth) && Number(item.periodYear) === Number(reportYear));
      appendDataset(fallbackPeriodItems);
      const merged = Array.from(mergedById.values());
      const periodDataset = merged.filter((item) => Number(item.periodMonth) === Number(reportMonth) && Number(item.periodYear) === Number(reportYear));
      dataset = periodDataset;
      const managerCandidates = (dataset || []).filter(isManagerRegister);
      if (!managerCandidates.length) {
        alert('Нет ведомостей менеджеров за выбранный месяц.');
        return;
      }
      const effectiveSummaryMap = buildSummaryMap(dataset || []);
      const sections = [];
      for (const reg of managerCandidates) {
        const detailResp = await fetch(`${API_BASE}/api/payrolls/${reg.id}`, { headers: { ...authHeaders } });
        const detailData = await detailResp.json();
        if (!detailResp.ok) throw new Error(detailData?.error || 'detail_failed');
        const detailStatus = String(detailData.status || '').trim().toLowerCase();
        if (!finalRegisterStatuses.has(detailStatus)) {
          continue;
        }
        const rows = Array.isArray(detailData.rows) ? detailData.rows : [];
        let previewData = null;
        if (detailData.managerUserId) {
          const params = new URLSearchParams();
          params.set('managerUserId', detailData.managerUserId);
          params.set('month', detailData.periodMonth || reportMonth || now.getMonth() + 1);
          params.set('year', detailData.periodYear || reportYear || now.getFullYear());
          try {
            const previewResp = await fetch(`${API_BASE}/api/payrolls/manager/preview?${params.toString()}`, { headers: { ...authHeaders } });
            const previewJson = await previewResp.json();
            if (!previewResp.ok) throw new Error(previewJson?.error || 'preview_failed');
            previewData = previewJson;
          } catch (err) {
            console.warn('manager preview for report failed', err);
          }
        }
        const relatedCardIds = new Set(
          (previewData?.objects || [])
            .map((obj) => Number(obj.cardId) || 0)
            .filter((cardId) => cardId > 0)
        );

        let objectRegisters = [];
        if (relatedCardIds.size) {
          const perCard = [];
          relatedCardIds.forEach((cardId) => {
            const summary = effectiveSummaryMap.get(cardId);
            if (summary) {
              perCard.push({ ...summary });
            }
          });
          perCard.sort((a, b) => String(a.objectName || '').localeCompare(String(b.objectName || '')));
          objectRegisters = perCard;
        }

        if (!objectRegisters.length && Array.isArray(previewData?.objects)) {
          const filteredPreview = (previewData.objects || []).filter((obj) => {
            const cardId = Number(obj.cardId) || 0;
            return cardId > 0 && effectiveSummaryMap.has(cardId);
          });
          objectRegisters = filteredPreview.map((obj) => {
            const summary = effectiveSummaryMap.get(Number(obj.cardId) || 0);
            return summary ? { ...summary } : null;
          }).filter(Boolean);
        }

        // Объекты для менеджера выводим только при наличии финальной штатной ведомости
        objectRegisters = objectRegisters.filter((reg) => reg && reg.cardId && effectiveSummaryMap.has(reg.cardId));

        if (!objectRegisters.length) {
          continue;
        }
        const managerTotals = rows.reduce((acc, row) => ({
          paidToCard: round2(acc.paidToCard + (Number(row.paidToCard) || 0)),
          advanceAmount: round2(acc.advanceAmount + (Number(row.advanceAmount) || 0)),
          incomeAmount: round2(acc.incomeAmount + (Number(row.incomeAmount) || 0)),
          toPayAmount: round2(acc.toPayAmount + (Number(row.toPayAmount ?? ((Number(row.incomeAmount) || 0) - (Number(row.advanceAmount) || 0) - (Number(row.paidToCard) || 0))))),
        }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });
        const objectTotals = objectRegisters.reduce((acc, reg) => ({
          paidToCard: round2(acc.paidToCard + (Number(reg.totalPaidCard) || 0)),
          advanceAmount: round2(acc.advanceAmount + (Number(reg.totalAdvance) || 0)),
          incomeAmount: round2(acc.incomeAmount + (Number(reg.totalIncome) || 0)),
          toPayAmount: round2(acc.toPayAmount + (Number(reg.totalToPay) || 0)),
        }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });
        const combinedTotals = {
          paidToCard: round2(managerTotals.paidToCard + objectTotals.paidToCard),
          advanceAmount: round2(managerTotals.advanceAmount + objectTotals.advanceAmount),
          incomeAmount: round2(managerTotals.incomeAmount + objectTotals.incomeAmount),
          toPayAmount: round2(managerTotals.toPayAmount + objectTotals.toPayAmount),
        };
        const sheetPayload = {
          ...detailData,
          objectName: detailData.managerInfo?.name || detailData.objectName || '',
          managerUserId: detailData.managerUserId,
        };
        const html = buildManagerWithObjectsDocument(sheetPayload, rows, previewData || undefined, objectRegisters);
        sections.push({
          managerId: detailData.managerUserId || detailData.objectCode,
          managerName: sheetPayload.objectName || `ID ${detailData.managerUserId || ''}`,
          html,
          totals: { managerTotals, objectTotals, combinedTotals },
          objectRegisters,
        });
      }
      if (!sections.length) {
        alert('Нет подписанных или выданных ведомостей менеджеров за выбранный месяц.');
        return;
      }
      sections.sort((a, b) => String(a.managerName || '').localeCompare(String(b.managerName || '')));
      const monthTitle = monthNames[(Number(reportMonth) || 1) - 1] || reportMonth;

      const managerSummaryTotals = sections.reduce((acc, section) => ({
        paidToCard: round2(acc.paidToCard + section.totals.managerTotals.paidToCard),
        advanceAmount: round2(acc.advanceAmount + section.totals.managerTotals.advanceAmount),
        incomeAmount: round2(acc.incomeAmount + section.totals.managerTotals.incomeAmount),
        toPayAmount: round2(acc.toPayAmount + section.totals.managerTotals.toPayAmount),
      }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });

      const managerSummaryRows = sections.map((section, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(section.managerName || section.managerId || '—')}</td>
          <td style="text-align:right">${currency(section.totals.managerTotals.paidToCard)} ₽</td>
          <td style="text-align:right">${currency(section.totals.managerTotals.advanceAmount)} ₽</td>
          <td style="text-align:right">${currency(section.totals.managerTotals.incomeAmount)} ₽</td>
          <td style="text-align:right">${currency(section.totals.managerTotals.toPayAmount)} ₽</td>
        </tr>
      `).join('');

      const objectsSummaryMap = new Map();
      sections.forEach((section) => {
        (section.objectRegisters || []).forEach((reg) => {
          const key = `${reg.objectCode || ''}::${reg.objectName || ''}`;
          const existing = objectsSummaryMap.get(key) || {
            objectName: reg.objectName || 'Объект',
            objectCode: reg.objectCode || '',
            paidToCard: 0,
            advanceAmount: 0,
            incomeAmount: 0,
            toPayAmount: 0,
          };
          existing.paidToCard = round2(existing.paidToCard + (Number(reg.totalPaidCard) || 0));
          existing.advanceAmount = round2(existing.advanceAmount + (Number(reg.totalAdvance) || 0));
          existing.incomeAmount = round2(existing.incomeAmount + (Number(reg.totalIncome) || 0));
          existing.toPayAmount = round2(existing.toPayAmount + (Number(reg.totalToPay) || 0));
          objectsSummaryMap.set(key, existing);
        });
      });
      const objectEntries = [...objectsSummaryMap.values()].sort((a, b) => String(a.objectName || '').localeCompare(String(b.objectName || '')));
      const objectSummaryTotals = objectEntries.reduce((acc, entry) => ({
        paidToCard: round2(acc.paidToCard + entry.paidToCard),
        advanceAmount: round2(acc.advanceAmount + entry.advanceAmount),
        incomeAmount: round2(acc.incomeAmount + entry.incomeAmount),
        toPayAmount: round2(acc.toPayAmount + entry.toPayAmount),
      }), { paidToCard: 0, advanceAmount: 0, incomeAmount: 0, toPayAmount: 0 });
      const objectSummaryRows = objectEntries.map((entry, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(entry.objectName || entry.objectCode || 'Объект')}</td>
          <td style="text-align:right">${currency(entry.paidToCard)} ₽</td>
          <td style="text-align:right">${currency(entry.advanceAmount)} ₽</td>
          <td style="text-align:right">${currency(entry.incomeAmount)} ₽</td>
          <td style="text-align:right">${currency(entry.toPayAmount)} ₽</td>
        </tr>
      `).join('');

      const grandTotals = {
        paidToCard: round2(managerSummaryTotals.paidToCard + objectSummaryTotals.paidToCard),
        advanceAmount: round2(managerSummaryTotals.advanceAmount + objectSummaryTotals.advanceAmount),
        incomeAmount: round2(managerSummaryTotals.incomeAmount + objectSummaryTotals.incomeAmount),
        toPayAmount: round2(managerSummaryTotals.toPayAmount + objectSummaryTotals.toPayAmount),
      };

      const managerTableHtml = `
        <h2 style="margin:24px 0 8px;">Менеджеры</h2>
        <table>
          <thead>
            <tr>
              <th style="width:60px;">#</th>
              <th>ФИО менеджера</th>
              <th>Выплачено на карту</th>
              <th>Аванс</th>
              <th>Итого доход</th>
              <th>К выплате</th>
            </tr>
          </thead>
          <tbody>
            ${managerSummaryRows || '<tr><td colspan="6" style="text-align:center;color:#94a3b8;">Нет менеджеров в отчёте</td></tr>'}
            <tr class="totals">
              <td colspan="2">ИТОГО менеджеры</td>
              <td style="text-align:right">${currency(managerSummaryTotals.paidToCard)} ₽</td>
              <td style="text-align:right">${currency(managerSummaryTotals.advanceAmount)} ₽</td>
              <td style="text-align:right">${currency(managerSummaryTotals.incomeAmount)} ₽</td>
              <td style="text-align:right">${currency(managerSummaryTotals.toPayAmount)} ₽</td>
            </tr>
          </tbody>
        </table>
      `;

      const objectsTableHtml = `
        <h2 style="margin:24px 0 8px;">Объекты</h2>
        <table>
          <thead>
            <tr>
              <th style="width:60px;">#</th>
              <th>Название объекта</th>
              <th>Выплачено на карту</th>
              <th>Аванс</th>
              <th>Итого доход</th>
              <th>К выплате</th>
            </tr>
          </thead>
          <tbody>
            ${objectSummaryRows || '<tr><td colspan="6" style="text-align:center;color:#94a3b8;">Нет объектов в отчёте</td></tr>'}
            <tr class="totals">
              <td colspan="2">ИТОГО объекты</td>
              <td style="text-align:right">${currency(objectSummaryTotals.paidToCard)} ₽</td>
              <td style="text-align:right">${currency(objectSummaryTotals.advanceAmount)} ₽</td>
              <td style="text-align:right">${currency(objectSummaryTotals.incomeAmount)} ₽</td>
              <td style="text-align:right">${currency(objectSummaryTotals.toPayAmount)} ₽</td>
            </tr>
          </tbody>
        </table>
      `;

      const totalsTableHtml = `
        <h2 style="margin:24px 0 8px;">Сводные суммы</h2>
        <table>
          <thead>
            <tr>
              <th>Категория</th>
              <th>Выплачено на карту</th>
              <th>Аванс</th>
              <th>Итого доход</th>
              <th>К выплате</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Менеджеры</td>
              <td style="text-align:right">${currency(managerSummaryTotals.paidToCard)} ₽</td>
              <td style="text-align:right">${currency(managerSummaryTotals.advanceAmount)} ₽</td>
              <td style="text-align:right">${currency(managerSummaryTotals.incomeAmount)} ₽</td>
              <td style="text-align:right">${currency(managerSummaryTotals.toPayAmount)} ₽</td>
            </tr>
            <tr>
              <td>Объекты</td>
              <td style="text-align:right">${currency(objectSummaryTotals.paidToCard)} ₽</td>
              <td style="text-align:right">${currency(objectSummaryTotals.advanceAmount)} ₽</td>
              <td style="text-align:right">${currency(objectSummaryTotals.incomeAmount)} ₽</td>
              <td style="text-align:right">${currency(objectSummaryTotals.toPayAmount)} ₽</td>
            </tr>
            <tr class="totals">
              <td>Всего</td>
              <td style="text-align:right">${currency(grandTotals.paidToCard)} ₽</td>
              <td style="text-align:right">${currency(grandTotals.advanceAmount)} ₽</td>
              <td style="text-align:right">${currency(grandTotals.incomeAmount)} ₽</td>
              <td style="text-align:right">${currency(grandTotals.toPayAmount)} ₽</td>
            </tr>
          </tbody>
        </table>
      `;

      const summaryHtml = `
        <h1 style="text-align:center;margin:0 0 12px;">Полный отчёт по менеджерам</h1>
        <div style="margin-bottom:12px;font-size:14px;">Период: <strong>${escapeHtml(monthTitle)} ${reportYear}</strong></div>
        ${managerTableHtml}
        ${objectsTableHtml}
        ${totalsTableHtml}
      `;
      const reportHtml = sections
        .map((section, idx) => (idx === sections.length - 1 ? section.html : `${section.html}<div class="page-break"></div>`))
        .join('');
      const fullHtml = `${summaryHtml}${sections.length ? '<div class="page-break"></div>' : ''}${reportHtml}`;
      openPrintWindow(fullHtml);
    } catch (error) {
      console.error('full manager report error', error);
      const details = error?.message ? `\n${error.message}` : '';
      alert(`Не удалось сформировать полный отчёт менеджеров.${details}`);
    } finally {
      setReportLoading(false);
    }
  }, [list, authHeaders, reportMonth, reportYear]);

  const infoBarStyle = { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' };
  const filterInputStyle = { width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5f5', fontSize: 13 };
  const columnFilterInputStyle = { width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #d4d8e5', fontSize: 12 };
  const tableWrapperStyle = { borderRadius: 18, border: '1px solid #e5e7eb', overflowX: 'auto', boxShadow: '0 25px 45px rgba(15,23,42,0.08)' };
  const thStyle = { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', textAlign: 'left', fontSize: 13, fontWeight: 700 };
  const tdStyle = { padding: '10px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 14, verticalAlign: 'top' };
  const journalLinkStyle = { color: '#1d4ed8', textDecoration: 'underline', fontWeight: 600 };
  return (
    <div style={{ padding: '28px 20px' }}>
      <h2 style={{ marginTop: 0, marginBottom: 14 }}>Журнал ведомостей выплат</h2>

      <div style={{ ...infoBarStyle, marginBottom: 16 }}>
        <button onClick={handleRefresh} style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>Обновить</button>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>Отчёт за</span>
          <button
            type="button"
            onClick={() => shiftReportPeriod(-1)}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer' }}
            title="Предыдущий месяц"
          >
            ←
          </button>
          <select value={reportMonth} onChange={(e) => setReportMonth(Number(e.target.value))} style={{ ...filterInputStyle, width: 160 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{monthNames[m - 1]}</option>
            ))}
          </select>
          <input
            type="number"
            value={reportYear}
            onChange={(e) => setReportYear(Number(e.target.value))}
            style={{ ...filterInputStyle, width: 120 }}
          />
          <button
            type="button"
            onClick={() => shiftReportPeriod(1)}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer' }}
            title="Следующий месяц"
          >
            →
          </button>
          <button
            onClick={handleOpenMonthlyReport}
            disabled={reportLoading || loading}
            style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #0f172a', background: '#f8fafc', color: '#0f172a', fontWeight: 600, opacity: reportLoading ? 0.7 : 1 }}
          >
            {reportLoading ? 'Сбор отчёта…' : 'Полный отчёт'}
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => handleCreate('standard')} style={{ padding: '10px 14px', borderRadius: 10, border: '1px dashed #16a34a', background: '#f0fdf4', fontWeight: 600 }}>+ Новая ведомость</button>
        <button onClick={() => handleCreate('manager')} style={{ padding: '10px 14px', borderRadius: 10, border: '1px dashed #7c3aed', background: '#f5f3ff', color: '#5b21b6', fontWeight: 600 }}>+ Ведомость менеджера</button>
      </div>

      {error && <div style={{ marginBottom: 12, color: '#b91c1c' }}>{error}</div>}

      <div style={{ marginBottom: 10, fontSize: 13, color: '#475569' }}>
        Найдено: <strong>{filteredList.length}</strong> · Сумма ведомостей: <strong>{currency(totals)} ₽</strong>
      </div>

      <div style={tableWrapperStyle} className="journal-card">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }} className="journal-table">
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 90 }}>ID</th>
              <th style={{ ...thStyle, width: 240 }}>Объект</th>
              <th style={{ ...thStyle, width: 220 }}>Менеджер</th>
              <th style={thStyle}>Период</th>
              <th style={thStyle}>Тип</th>
              <th style={thStyle}>Статус</th>
              <th style={thStyle}>Сумма ведомости</th>
              <th style={thStyle}>Примечание</th>
              <th style={{ ...thStyle, width: 120 }}>Действие</th>
            </tr>
            <tr>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.id}
                  onChange={(e) => handleColumnFilterChange('id', e.target.value)}
                  placeholder="Поиск..."
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.object}
                  onChange={(e) => handleColumnFilterChange('object', e.target.value)}
                  placeholder="Название / код"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.manager}
                  onChange={(e) => handleColumnFilterChange('manager', e.target.value)}
                  placeholder="ФИО менеджера"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.period}
                  onChange={(e) => handleColumnFilterChange('period', e.target.value)}
                  placeholder="мм гггг"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.type}
                  onChange={(e) => handleColumnFilterChange('type', e.target.value)}
                  placeholder="Тип"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.status}
                  onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                  placeholder="Статус"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.amount}
                  onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                  placeholder="Сумма"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <input
                  value={columnFilters.note}
                  onChange={(e) => handleColumnFilterChange('note', e.target.value)}
                  placeholder="Примечание"
                  style={columnFilterInputStyle}
                />
              </th>
              <th style={{ ...thStyle, background: '#fff' }}>
                <button
                  type="button"
                  onClick={resetColumnFilters}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Сбросить
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Загрузка журнала…</td></tr>
            ) : filteredList.length === 0 ? (
              <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>Нет ведомостей по заданным условиям</td></tr>
            ) : (
              filteredList.map((item, idx) => {
                const badge = registerTypeBadges[item.registerType || 'standard'] || registerTypeBadges.standard;
                const regStatus = item.status || 'draft';
                const regBadge = registerStatusBadges[regStatus] || registerStatusBadges.draft;
                return (
                  <tr
                    key={item.id}
                    onClick={(e) => { fillFilterByCell('object', item.objectName, e); }}
                    onDoubleClick={() => handleOpen(item.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }} onClick={(e) => fillFilterByCell('id', item.id, e)}>
                      {item.id ? (
                        <Link to={`/payrolls/${item.id}`} style={journalLinkStyle}>
                          #{item.id}
                        </Link>
                      ) : '—'}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{item.objectName || 'Без названия'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }} onClick={(e) => fillFilterByCell('object', item.objectCodeFull || item.objectCode, e)}>Код: {item.objectCodeFull || item.objectCode || '—'}</div>
                      {item.id && (
                        <div style={{ marginTop: 6 }}>
                          <a
                            href={`/payrolls/${item.id}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpen(item.id); }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 10px',
                              borderRadius: 999,
                              border: '1px solid #cbd5f5',
                              background: '#f8fafc',
                              color: '#1d4ed8',
                              fontSize: 12,
                              fontWeight: 600,
                              textDecoration: 'none',
                            }}
                          >
                            Ведомость #{item.id}
                          </a>
                        </div>
                      )}
                    </td>
                    <td style={tdStyle} onClick={(e) => fillFilterByCell('manager', item.managerName, e)}>
                      {item.managerName ? (
                        <span style={{ fontWeight: 600 }}>{item.managerName}</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle} onClick={(e) => fillFilterByCell('period', `${shortMonthNames[(item.periodMonth || 1) - 1]} ${item.periodYear}`, e)}>
                      {shortMonthNames[(item.periodMonth || 1) - 1]} {item.periodYear}
                    </td>
                    <td style={tdStyle} onClick={(e) => fillFilterByCell('type', registerTypeLabels[item.registerType || 'standard'], e)}>
                      <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: badge.bg, color: badge.color }}>
                        {registerTypeLabels[item.registerType || 'standard']}
                      </span>
                    </td>
                    <td style={tdStyle} onClick={(e) => fillFilterByCell('status', registerStatusLabels[regStatus] || regStatus, e)}>
                      <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: regBadge.bg, color: regBadge.color }}>
                        {registerStatusLabels[regStatus] || 'Черновик'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }} onClick={(e) => fillFilterByCell('amount', item.totalToPay, e)}>{currency(item.totalToPay)} ₽</td>
                    <td style={tdStyle} onClick={(e) => fillFilterByCell('note', item.notes || item.title, e)}>{item.notes || item.title || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                        disabled={deletingId === item.id}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ef4444', background: deletingId === item.id ? '#fee2e2' : '#fef2f2', color: '#b91c1c', fontWeight: 600 }}
                      >
                        {deletingId === item.id ? 'Удаление…' : 'Удалить'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 13, color: '#475569', textAlign: 'right' }}>
        Строк: <strong>{filteredList.length}</strong>
      </div>
    </div>
  );
}
